import { Instance, types, flow } from "mobx-state-tree";
import axios from "axios";
import { PAYMENTS_API } from "@constants/api";
import { AlertResultApi } from "@utils/alert";
import { fetching, FetchingEnum } from "./Fetching";
import { Pagination } from "./Pagination";
import { jsonParsePagination } from "@utils/format";

export const initialStateOnProcess = {
  fetchingOnProcess: FetchingEnum.DONE,
  fetchingOnProcessMore: FetchingEnum.DONE,
  fetchingDeleteOnProcess: FetchingEnum.DONE,
};

const AppliedCreditNote = types.model("appliedCreditNote", {
  amount: types.maybeNull(types.number),
  cnAccountingId: types.maybeNull(types.number),
  description: types.maybeNull(types.string),
  id: types.maybeNull(types.number),
});

const invoice = types.model({
  id: types.maybeNull(types.number),
  paymentId: types.maybeNull(types.number),
  mbcRef: types.maybeNull(types.string),
  amount: types.maybeNull(types.number),
  description: types.maybeNull(types.string),
  projectCode: types.maybeNull(types.string),
  schoolCode: types.maybeNull(types.string),
  tbfCode: types.maybeNull(types.string),
  appliedCreditNotes: types.maybeNull(types.array(AppliedCreditNote)),
});

const onProcessPayment = types.model({
  id: types.maybeNull(types.number),
  paymentPlatform: types.maybeNull(types.string),
  paymentRef: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  url: types.maybeNull(types.string),
  callbackLog: types.maybeNull(types.string),
  amount: types.maybeNull(types.number),
  invoices: types.maybeNull(types.array(invoice)),
  expiredAt: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
});

export const OnProcess = types
  .model({
    fetchingOnProcess: fetching,
    onProcessPayment: types.maybeNull(types.array(onProcessPayment)),
    statusOnProcess: types.maybeNull(types.number),
    paginationOnProcess: types.maybeNull(Pagination),
    fetchingOnProcessMore: fetching,
    fetchingDeleteOnProcess: fetching,
  })
  .actions((self) => ({
    getOnProcess: flow(function* (token: string, pageNumber?: number) {
      if (pageNumber) {
        self.fetchingOnProcessMore = FetchingEnum.PENDING;
      } else {
        self.fetchingOnProcess = FetchingEnum.PENDING;
        self.onProcessPayment = null;
        self.statusOnProcess = null;
      }
      try {
        const result = yield axios.get(PAYMENTS_API, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            Filter: JSON.stringify({
              StatusIn: ["New", "Paid"],
              PageSize: 10,
              PageNumber: pageNumber,
            }),
          },
        });
        self.paginationOnProcess = jsonParsePagination(
          result.headers["x-pagination"]
        );
        if (pageNumber && self.onProcessPayment && result.data) {
          const temp: any = self.onProcessPayment;
          self.onProcessPayment = temp.concat(result.data);
        } else {
          self.onProcessPayment = result.data;
        }
        self.fetchingOnProcess = "done";
        self.statusOnProcess = result.status;
        self.fetchingOnProcessMore = FetchingEnum.DONE;
      } catch (error) {
        self.fetchingOnProcess = "error";
        self.fetchingOnProcessMore = FetchingEnum.ERROR;
        AlertResultApi(error, error.response?.data?.message);
        self.statusOnProcess = error.response?.status;
      }
    }),
    deleteOnProcess: flow(function* (
      token: string,
      id: number,
      callback: () => void
    ) {
      self.fetchingDeleteOnProcess = "pending";
      try {
        const result = yield axios.delete(`${PAYMENTS_API}/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        self.fetchingDeleteOnProcess = "done";
        self.statusOnProcess = result.status;
        callback();
      } catch (error) {
        self.fetchingDeleteOnProcess = "error";
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
  }))
  .views((self) => ({
    getItemInvoiceProcessing(url: string) {
      const findIndex = self.onProcessPayment
        ? self.onProcessPayment.findIndex((item) => item.url === url)
        : -1;

      return findIndex !== -1 && self.onProcessPayment
        ? self.onProcessPayment[findIndex]
        : null;
    },
  }));

export type OnProcessType = Instance<typeof OnProcess>;
export type onProcessItemType = Instance<typeof onProcessPayment>;
export type onProcessinvoiceType = Instance<typeof invoice>;
